import React, { useState, useEffect, useContext } from "react";
import { FaTimes } from "react-icons/fa"; // Close icon
import { UserContext } from "../context/UserContext"; // Access user details for emailId
import AddTutorialVideo from "../sections/AddTutorialVideo"; // Import AddTutorialVideo component

const VideosAdmin = ({ onClose }) => {
  const [videos, setVideos] = useState([]); // Videos state
  const { emailId, isLoggedIn } = useContext(UserContext); // Get user details
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [showEditPopup, setShowEditPopup] = useState(null); // Tracks the video ID for edit popup
  const [showDeletePopup, setShowDeletePopup] = useState(null); // Tracks the video ID for delete popup
  const [searchTerm, setSearchTerm] = useState(""); // For dynamic video filtering
  const [successMessage, setSuccessMessage] = useState(""); // Tracks success message for delete action
  const [editVideoData, setEditVideoData] = useState(null); // Stores video data for editing

  useEffect(() => {
    if (!isLoggedIn || emailId !== "urvote_tutorials@urcommunity.com.au") return;

    const fetchVideos = async () => {
      setIsLoading(true);
      try {
        const response = await fetch("/api/tutorialvideos");
        if (!response.ok) throw new Error("Failed to fetch videos");

        const data = await response.json();
        setVideos(data); // Set fetched videos
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideos();
  }, [emailId, isLoggedIn]);

  const handleDelete = async (videoId) => {
    try {
      const response = await fetch(`/api/tutorialvideos/${videoId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Video not found");
        }
        throw new Error("Failed to delete video");
      }

      setVideos((prevVideos) => prevVideos.filter((video) => video.id !== videoId));
      setSuccessMessage("Video successfully deleted!");
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error deleting video:", error);
      alert(error.message || "Failed to delete video. Please try again.");
    } finally {
      setShowDeletePopup(null);
    }
  };

  // Fetch video details for editing
const fetchVideoDetails = async (videoId) => {
    try {
      const response = await fetch(`/api/tutorialvideos/${videoId}`);
      if (!response.ok) throw new Error("Failed to fetch video details");
  
      const data = await response.json();
      setEditVideoData(data); // Store video data for editing
  
      // Ensure popup opens only after editVideoData is set
      setTimeout(() => setShowEditPopup(videoId), 100);
    } catch (error) {
      console.error("Error fetching video details:", error);
      alert("Failed to fetch video details. Please try again.");
    }
  };
 

  // Handle Edit button click
  const handleEdit = (videoId) => {
    fetchVideoDetails(videoId);
  };

  // Handle Save Changes (to update video)
  const handleSaveChanges = async (updatedVideo) => {
    try {
      const response = await fetch(
        `/api/tutorialvideos/${showEditPopup}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedVideo),
        }
      );
      if (!response.ok) throw new Error("Failed to update video");

      // Update state to reflect changes without reloading
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video.id === showEditPopup ? { ...video, ...updatedVideo } : video
        )
      );

      setShowEditPopup(null); // Close the popup
      alert("Video updated successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes. Please try again.");
    }
  };

  const getFilteredVideos = () => {
    const searchValue = searchTerm.toLowerCase().trim();
    if (!searchValue) return videos;
    return videos.filter((video) => video.title.toLowerCase().includes(searchValue));
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)", // Overlay
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999,
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "10px",
          padding: "20px",
          width: "95%",
          maxWidth: "800px",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          position: "relative",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h3 style={{ margin: 0 }}>Videos Admin</h3>
          <FaTimes
            onClick={onClose}
            style={{ cursor: "pointer", fontSize: "1.5rem", color: "#333" }}
          />
        </div>

        {/* Success Message */}
        {successMessage && (
          <div
            style={{
              textAlign: "center",
              marginBottom: "20px",
              padding: "10px",
              backgroundColor: "#d4edda",
              color: "#155724",
              borderRadius: "5px",
            }}
          >
            {successMessage}
          </div>
        )}

        {/* Search Bar */}
        <div className="search-bar" style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
          <input
            type="text"
            placeholder="Search videos..."
            className="search-input"
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              outline: "none",
            }}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          />
        </div>

        {/* Video List */}
        {isLoading ? (
          <p style={{ textAlign: "center", color: "#555" }}>Loading videos...</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "20px" }}>
            {getFilteredVideos().map((video) => (
              <div
                key={video.id}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                  padding: "10px",
                  position: "relative",
                }}
              >
                {/* Video Info */}
                <h5 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "5px" }}>
                  {video.title}
                </h5>
                <p style={{ fontSize: "14px", color: "#555", marginBottom: "5px" }}>{video.description}</p>

                {/* Video Preview */}
                <video style={{ width: "100%", borderRadius: "8px" }} controls>
                  <source src={video.video_url} type="video/mp4" />
                </video>

                {/* Buttons (Edit & Delete in one row) */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
                  <button
                    onClick={() => handleEdit(video.id)}
                    style={{
                      backgroundColor: "blue",
                      color: "#fff",
                      border: "none",
                      borderRadius: "5px",
                      padding: "5px 10px",
                      cursor: "pointer",
                      flex: "1",
                      marginRight: "5px",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeletePopup(video.id)}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "#fff",
                      border: "none",
                      borderRadius: "5px",
                      padding: "5px 10px",
                      cursor: "pointer",
                      flex: "1",
                    }}
                  >
                    Delete
                  </button>
                </div>

                {/* Delete Confirmation Popup */}
                {showDeletePopup === video.id && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                      padding: "20px",
                      textAlign: "center",
                      zIndex: 1000,
                      width: "100%",
                    }}
                  >
                    <p style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "20px" }}>
                      Are you sure you want to delete this video?
                    </p>
                    <button
                      onClick={() => setShowDeletePopup(null)}
                      style={{
                        backgroundColor: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        padding: "10px 20px",
                        cursor: "pointer",
                        marginRight: "10px",
                      }}
                    >
                      No
                    </button>
                    <button
                      onClick={() => handleDelete(video.id)}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        padding: "10px 20px",
                        cursor: "pointer",
                      }}
                    >
                      Yes
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Edit Video Popup */}
        {showEditPopup && (
          <AddTutorialVideo
            video={editVideoData}
            onClose={() => setShowEditPopup(null)}
            handleSubmit={handleSaveChanges}
          />
        )}
      </div>
    </div>
  );
};

export default VideosAdmin;
